import { Task } from "@repo/schema";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useImageLoader } from "../../hooks/useImageLoader";
import { Annotation, ToolMode } from "../../types/annotations";
import { KonvaStage, KonvaStageHandle, PreviewBoxSpec } from "./KonvaStage";

export interface CanvasHandle {
  resetView: () => void;
  startNudge: (selectedIds: Set<string>) => void;
  applyNudge: (dxPx: number, dyPx: number) => void;
  clearNudge: () => void;
}

export interface CanvasProps {
  task: Task | undefined;
  annotations: Annotation[];
  selectedAnnotationIds: Set<string>;
  primarySelectedId: string | null;
  toolMode: ToolMode;
  activeLabel: { id: string; name: string; color: string } | null;
  scale: number;
  position: { x: number; y: number };
  onSelect: (id: string | null, opts?: { additive?: boolean }) => void;
  onAddAnnotation: (annotation: Annotation) => void;
  onUpdateAnnotation: (id: string, updates: Partial<Annotation>) => void;
  onDeleteSelected: () => void;
  onCopySelection: () => void;
  onPasteClipboard: () => void;
  onGroupSelected: () => void;
  onUngroupSelected: () => void;
  canGroup: boolean;
  onGroupTranslate: (
    updates: Array<{ id: string; updates: Partial<Annotation> }>,
  ) => void;
  onUndo: () => void;
  onRedo: () => void;
  onZoomAtPoint: (pointer: { x: number; y: number }, factor: number) => void;
  onSetPosition: (pos: { x: number; y: number }) => void;
  onFitImage: (iw: number, ih: number, cw: number, ch: number) => void;
  /** Is there anything to save? Staged auto-annotations do not count: Save
   * keeps them staged, only Approve commits them. */
  canSave: boolean;
  isSaving: boolean;
  onSave: () => void;
  onSaveEmpty: () => void;
  showCrosshair: boolean;
  /** The image carries staged auto-annotations nobody has approved yet. */
  awaitingReview: boolean;
  /** Promote the staged auto-annotations and save the image. */
  onApprove: () => void;
  onImageLoad?: (width: number, height: number) => void;
  /** When true, disables all editing (drawing, save, delete) and renders inferred regions as dashed+score. */
  readOnly?: boolean;
  /** Boxes a live auto-annotation run has emitted so far. See `PreviewBox`. */
  previewBoxes?: PreviewBoxSpec[];
}

const isTextInputFocused = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return (
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.isContentEditable
  );
};

export const Canvas = forwardRef<CanvasHandle, CanvasProps>(({
  task,
  annotations,
  selectedAnnotationIds,
  primarySelectedId,
  toolMode,
  activeLabel,
  scale,
  position,
  onSelect,
  onAddAnnotation,
  onUpdateAnnotation,
  onDeleteSelected,
  onCopySelection,
  onPasteClipboard,
  onGroupSelected,
  onUngroupSelected,
  canGroup,
  onGroupTranslate,
  onUndo,
  onRedo,
  onZoomAtPoint,
  onSetPosition,
  onFitImage,
  canSave,
  isSaving,
  onSave,
  onSaveEmpty,
  showCrosshair,
  awaitingReview,
  onApprove,
  onImageLoad,
  readOnly = false,
  previewBoxes,
}, ref) => {
  const { t } = useTranslation(["label", "common"]);
  const containerRef = useRef<HTMLDivElement>(null);
  const stageHandle = useRef<KonvaStageHandle>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [selectedVertex, setSelectedVertex] = useState<{ annotationId: string; index: number } | null>(null);
  const { image, loading, error } = useImageLoader(task?.filePath);
  const fittedImageRef = useRef<HTMLImageElement | null>(null);

  useImperativeHandle(ref, () => ({
    resetView: () => {
      const container = containerRef.current;
      if (!container || !image) return;
      onFitImage(image.width, image.height, container.clientWidth, container.clientHeight);
    },
    startNudge: (ids) => stageHandle.current?.startNudge(ids),
    applyNudge: (dx, dy) => stageHandle.current?.applyNudge(dx, dy),
    clearNudge: () => stageHandle.current?.clearNudge(),
  }), [image, onFitImage]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [task]);

  useEffect(() => {
    if (
      image &&
      containerSize.width > 0 &&
      containerSize.height > 0 &&
      fittedImageRef.current !== image
    ) {
      fittedImageRef.current = image;
      onFitImage(
        image.width,
        image.height,
        containerSize.width,
        containerSize.height,
      );
      onImageLoad?.(image.width, image.height);
    }
  }, [image, containerSize.width, containerSize.height]);

  useEffect(() => {
    setSelectedVertex(null);
  }, [selectedAnnotationIds]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const key = e.key;
      const mod = e.ctrlKey || e.metaKey;

      // Read-only view (inferred regions): selection exists but nothing may
      // mutate — only Escape (deselect) is allowed through.
      if (readOnly) {
        if (key === "Escape") {
          onSelect(null);
          setSelectedVertex(null);
        }
        return;
      }

      if (key === "Delete" || key === "Backspace") {
        if (selectedVertex !== null) {
          const ann = annotations.find((a) => a.id === selectedVertex.annotationId);
          if (ann?.points && ann.points.length > 3) {
            const newPoints = ann.points.filter((_, i) => i !== selectedVertex.index);
            onUpdateAnnotation(ann.id, { points: newPoints });
          }
          setSelectedVertex(null);
          e.preventDefault();
          return;
        }
        if (selectedAnnotationIds.size > 0) {
          e.preventDefault();
          onDeleteSelected();
        }
        return;
      }
      if (key === "Escape") {
        stageHandle.current?.cancelDrawing();
        onSelect(null);
        setSelectedVertex(null);
        return;
      }
      if (mod && (key === "z" || key === "Z")) {
        if (isTextInputFocused(e.target)) return;
        e.preventDefault();
        if (e.shiftKey || key === "Z") onRedo();
        else onUndo();
        return;
      }
      if (mod && key.toLowerCase() === "c") {
        if (isTextInputFocused(e.target)) return;
        if (selectedAnnotationIds.size === 0) return;
        e.preventDefault();
        onCopySelection();
        return;
      }
      if (mod && key.toLowerCase() === "v") {
        if (isTextInputFocused(e.target)) return;
        e.preventDefault();
        onPasteClipboard();
        return;
      }
      if (mod && key.toLowerCase() === "g") {
        if (isTextInputFocused(e.target)) return;
        e.preventDefault();
        if (e.shiftKey) {
          onUngroupSelected();
        } else if (canGroup) {
          onGroupSelected();
        }
        return;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    readOnly,
    selectedAnnotationIds,
    selectedVertex,
    annotations,
    onDeleteSelected,
    onUpdateAnnotation,
    onCopySelection,
    onPasteClipboard,
    onGroupSelected,
    onUngroupSelected,
    canGroup,
    onSelect,
    onUndo,
    onRedo,
  ]);

  if (!task) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-black/[0.03]">
        <div className="flex flex-1 items-center justify-center text-muted-foreground">
          <span className="text-base">{t("canvas.selectImage")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-black/[0.03]">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-black/10 px-4">
        <span
          className="min-w-0 flex-1 truncate text-sm font-medium"
          title={task.filePath.split("/").pop() ?? t("canvas.taskFallback")}
        >
          {task.filePath.split("/").pop() ?? t("canvas.taskFallback")}
        </span>
        {/* Approving saves too: whatever survives on the canvas is what the
            human signed off on. Save alone keeps the auto-annotations staged. */}
        {!readOnly && awaitingReview && (
          <button
            type="button"
            title={t("canvas.approveTitle")}
            className="shrink-0 cursor-pointer rounded border-none bg-emerald-500 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onApprove}
            disabled={isSaving}
          >
            {isSaving ? t("canvas.saving") : t("actions.approve")}
          </button>
        )}
        {!readOnly && annotations.length === 0 && (
          <button
            type="button"
            title={t("canvas.markEmptyTitle")}
            className="shrink-0 cursor-pointer rounded border border-primary bg-transparent px-3 py-1 text-xs font-semibold text-primary hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onSaveEmpty}
            disabled={isSaving}
          >
            {isSaving ? t("canvas.saving") : t("canvas.noObjects")}
          </button>
        )}
        {!readOnly && (
          <button
            type="button"
            title={t("canvas.saveTitle")}
            className="shrink-0 cursor-pointer rounded border-none bg-primary px-3 py-1 text-xs font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onSave}
            disabled={!canSave || isSaving}
          >
            {isSaving ? t("canvas.saving") : t("common:actions.save")}
          </button>
        )}
      </div>

      <div
        className="relative min-h-0 flex-1 overflow-hidden [&_canvas]:absolute [&_canvas]:left-0 [&_canvas]:top-0"
        ref={containerRef}
      >
        {loading && (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            <span className="text-sm">{t("canvas.loadingImage")}</span>
          </div>
        )}
        {error && (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            <span className="text-sm">{t(error)}</span>
          </div>
        )}
        {image && containerSize.width > 0 && (
          <div className="absolute left-0 top-0 h-full w-full">
            <KonvaStage
              ref={stageHandle}
              width={containerSize.width}
              height={containerSize.height}
              image={image}
              scale={scale}
              position={position}
              toolMode={toolMode}
              readOnly={readOnly}
              annotations={loading ? [] : annotations}
              selectedAnnotationIds={loading ? new Set() : selectedAnnotationIds}
              primarySelectedId={loading ? null : primarySelectedId}
              activeLabel={activeLabel}
              selectedVertex={selectedVertex}
              onSelect={onSelect}
              onAddAnnotation={onAddAnnotation}
              onUpdateAnnotation={onUpdateAnnotation}
              onVertexSelect={setSelectedVertex}
              onGroupTranslate={onGroupTranslate}
              onZoomAtPoint={onZoomAtPoint}
              onSetPosition={onSetPosition}
              showCrosshair={showCrosshair}
              previewBoxes={loading ? undefined : previewBoxes}
            />
          </div>
        )}
      </div>
    </div>
  );
});

Canvas.displayName = "Canvas";
